import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnyGameModule, SessionStats } from '../../core/contracts'
import { DEFAULT_PACE, paceFrom } from '../../core/contracts'
import { gameScore } from '../../core/scoring'
import { GameHost } from '../../shared/GameHost'
import { store, KEYS } from '../../platform/storage'
import { useT } from '../../platform/i18n/useT'
import { gameTitle } from '../gameText'
import './PlayScreen.css'

/**
 * Один заход в игру: играем -> сохраняем -> показываем результат.
 *
 * Экран знает про GameModule только через контракт, поэтому работает
 * с любой игрой без единой правки.
 */

interface Props {
  game: AnyGameModule
  onExit: () => void
  /**
   * Продолжение программы. Если задано, экран результата ведёт дальше
   * по тренировке дня вместо предложения переиграть.
   */
  onNext?: () => void
  /** Сколько упражнений останется после этого. */
  remaining?: number
  /** Итог захода наружу - для разбора тренировки. */
  onSession?: (stats: SessionStats, startLevel: number) => void
}

type Stage =
  | { kind: 'playing'; level: number; pace: number }
  | { kind: 'result'; stats: SessionStats; pace: number }

export function PlayScreen({ game, onExit, onNext, remaining, onSession }: Props) {
  const { t } = useT()
  const [stage, setStage] = useState<Stage | null>(null)
  const [scoreLine, setScoreLine] = useState<string | null>(null)
  /**
   * Текущая стадия отдельной ссылкой.
   *
   * Побочный эффект (onSession) нельзя вызывать внутри функции обновления
   * состояния: в строгом режиме React вызывает её дважды, и заход
   * записался бы в разбор тренировки два раза.
   */
  const stageRef = useRef<Stage | null>(null)
  stageRef.current = stage

  // поднимаем сохранённый уровень, чтобы не начинать каждый раз с нуля
  useEffect(() => {
    let alive = true
    void (async () => {
      let level = game.difficulty.initial
      let pace = DEFAULT_PACE
      try {
        const history = await store.get<SessionStats[]>(KEYS.gameStats(game.id), [])
        level = history[history.length - 1]?.level ?? level
        pace = paceFrom(history, game.referenceRtMs)
      } catch {
        // хранилище недоступно - начинаем с базовых значений, не блокируем игру
      }
      if (alive) setStage({ kind: 'playing', level, pace })
    })()
    return () => {
      alive = false
    }
  }, [game])

  const handleFinish = useCallback(
    (stats: SessionStats) => {
      const prev = stageRef.current
      const pace = prev?.kind === 'playing' ? prev.pace : DEFAULT_PACE

      if (prev?.kind === 'playing') onSession?.(stats, prev.level)
      setStage({ kind: 'result', stats, pace })
      void (async () => {
        try {
          const key = KEYS.gameStats(game.id)
          const history = await store.get<SessionStats[]>(key, [])
          // держим только последнее окно: CloudStorage ограничен по размеру значения
          const next = [...history, stats].slice(-20)
          await store.set(key, next)

          // диапазон нужен, иначе строка никогда не покажет
          // предварительную оценку и будет твердить «ещё N заходов»
          const score = gameScore(next, game.metric, game.difficulty)

          // проверяем статус первым: по значению TypeScript не сузит тип
          if (score.status === 'ready') {
            setScoreLine(`${t('index.title')}: ${score.value}`)
          } else if (score.provisional === null) {
            setScoreLine(t('index.collectingHint', { n: score.sessionsLeft }))
          } else {
            setScoreLine(
              `${t('index.title')}: ${score.provisional} · ${t('progress.collecting', {
                n: score.sessionsLeft,
              })}`,
            )
          }
        } catch {
          // результат уже показан, сохранение - не критичный путь
        }
      })()
    },
    [game, t, onSession],
  )

  if (!stage) return null

  if (stage.kind === 'playing') {
    return (
      <GameHost
        game={game}
        startLevel={stage.level}
        pace={stage.pace}
        onFinish={handleFinish}
      />
    )
  }

  const { stats } = stage
  const points = Math.round(stats.pc * 1000 + stats.span * 100)

  return (
    <div className="pr">
      <div className="pr__em">{gameTitle(t, game)}</div>
      <h2 className="pr__h">{t('result.sessionDone')}</h2>

      <div className="pr__pts">{points.toLocaleString('ru-RU')}</div>
      <div className="pr__pl">{t('result.points')}</div>

      <div className="pr__grid">
        <div className="pr__c">
          <div className="pr__k">{t('result.accuracy')}</div>
          <div className="pr__v">{Math.round(stats.pc * 100)}%</div>
        </div>
        <div className="pr__c">
          <div className="pr__k">{t('result.span')}</div>
          <div className="pr__v">{stats.span}</div>
        </div>
      </div>

      {scoreLine !== null && <p className="pr__score">{scoreLine}</p>}

      {onNext ? (
        <>
          <button type="button" className="pr__again" onClick={onNext}>
            {remaining && remaining > 0 ? t('workout.next') : t('workout.finish')}
          </button>
          <button type="button" className="pr__exit" onClick={onExit}>
            {t('result.finish')}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="pr__again"
            onClick={() => setStage({ kind: 'playing', level: stats.level, pace: stage.pace })}
          >
            {t('result.again')}
          </button>
          <button type="button" className="pr__exit" onClick={onExit}>
            {t('result.finish')}
          </button>
        </>
      )}
    </div>
  )
}
